/**
 * Normalizes a pathname into its route pattern — the URL with dynamic segments
 * replaced by param placeholders (`/channel123/cards/card456/update` →
 * `/:channelSlug/cards/:cardId/update`) — so long-task reports carry a
 * low-cardinality, aggregatable route tag. Raw pathnames embed
 * business/channel/entity slugs: high-cardinality, and workspace names don't
 * belong in a tag.
 *
 * Matches the URL shapes in `@shared/util/navigation`'s `browserRouteMapping`
 * rather than importing it: those are path builders (name → path), not
 * matchers, and this module must stay import-free — it runs inside Sentry
 * capture, which initializes before most of the app. Unrecognized shapes
 * collapse into a `/*` tail so the tag's value set stays bounded even when
 * routes change without this map keeping up.
 */
export function getRoutePatternForPathname(pathname: string): string {
  const segments = pathname.split('/').filter(segment => segment !== '');

  if (segments.length === 0) {
    return '/';
  }

  const [first, ...rest] = segments;

  if (KNOWN_STATIC_FIRST_SEGMENTS.has(first)) {
    return rest.length === 0 ? `/${first}` : `/${first}/*`;
  }

  // The first segment is a channel slug.
  return `/:channelSlug${getChannelPattern(rest)}`;
}

/** Non-channel first segments from `browserRouteMapping`. */
const KNOWN_STATIC_FIRST_SEGMENTS = new Set([
  'dashboard',
  'channels',
  'channel',
  'reports',
  'workflows',
  'section',
  'bundle',
  'onboarding',
  'newbusiness',
  'signin',
  'signup',
  'verify',
  'auth',
  'saml-auth',
  'playground',
]);

/** Channel sub-routes whose first segment is a fixed word with no dynamic tail. */
const STATIC_CHANNEL_SUB_SEGMENTS = new Set([
  'members',
  'notifications',
  'add',
  'duplicate',
  'reports',
]);

function getChannelPattern(segments: string[]): string {
  const [first, second, third] = segments;

  if (segments.length === 0) {
    return '';
  }
  if (first === 'messages') {
    return '/messages/*';
  }
  if (STATIC_CHANNEL_SUB_SEGMENTS.has(first)) {
    return segments.length === 1 ? `/${first}` : `/${first}/*`;
  }
  if (first !== 'cards') {
    return '/*';
  }

  if (second === 'create') {
    return third === 'capture' ? '/cards/create/capture' : '/cards/create';
  }
  if (second === 't') {
    return '/cards/t/:templateSlug';
  }
  if (second === 'infos' || second === 'trash') {
    return segments.length === 2 ? `/cards/${second}` : `/cards/${second}/*`;
  }
  if (second === undefined) {
    return '/cards';
  }

  // Anything else under /cards/ has a card id as its second segment.
  return `/cards/:cardId${getCardPattern(segments.slice(2))}`;
}

function getCardPattern(segments: string[]): string {
  const [first, second] = segments;

  if (first === 'history') {
    return '/history';
  }
  if (first !== 'update') {
    return '/*';
  }
  if (second === undefined) {
    return '/update';
  }
  if (second === 't') {
    return '/update/t/:templateSlug';
  }
  return '/update/:workflowTemplateId/bulk/:scheduleId/:allOrUpcoming';
}
